import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Navbar, Footer } from '../components/Navbar';
import { Logo } from '../components/Logo';
import { SoundtrackPicker } from '../components/SoundtrackPicker';
import { api } from '../lib/api';
import { Check, MapPin } from 'lucide-react';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '../components/ui/accordion';
import { usePageSEO } from '../lib/usePageSEO';
import { LANDING_FAQ_JSONLD } from '../lib/seoData';

export default function Landing() {
  usePageSEO({
    title: 'MyLeaf Legacy · Forever Here — Premium Digital Memorials & QR Plaques',
    description: 'Keep their story Forever Here. MyLeaf Legacy creates premium digital memorials with QR plaques, life-journey videos, olive family trees, and a private Family Vault — one beautiful, permanent home for the people you love.',
    path: '/',
    jsonLd: LANDING_FAQ_JSONLD,
  });
  return (
    <div className="min-h-screen">
      <Navbar />
      <SoundtrackPicker />

      {/* HERO — large logo as the centerpiece, single sentence, much air */}
      <section
        className="relative overflow-hidden"
        data-testid="landing-hero"
      >
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(680px 520px at 50% 18%, rgba(197,160,89,0.10), transparent 68%)',
          }}
        />
        <div className="ml-container-prose relative pt-24 pb-32 sm:pt-32 sm:pb-44 text-center">
          {/* Ambient olive leaves drifting slowly behind the logo — very low
              opacity, long duration. Emotional, not a UI effect. */}
          <FallingLeaves />

          <div className="flex justify-center fade-up relative">
            <div className="ml-hero-breathe">
              <Logo size={260} className="sm:hidden" />
              <Logo size={360} className="hidden sm:block" />
            </div>
          </div>
          <h1 className="font-serif font-light text-[34px] sm:text-5xl lg:text-[58px] leading-[1.15] tracking-[-0.015em] mt-16 sm:mt-20 text-[hsl(var(--primary))] fade-up fade-up-2">
            Some lives deserve<br />
            <span className="italic text-[hsl(var(--primary)/0.75)]">
              to be remembered forever.
            </span>
          </h1>
          <p
            className="mt-10 max-w-xl mx-auto text-[16px] sm:text-[17px] leading-relaxed text-[hsl(var(--primary))] font-medium fade-up fade-up-3"
            data-testid="hero-tagline"
          >
            A QR memorial for headstones, plaques, and funeral displays —
            so family and friends can visit their story, on any phone, forever.
          </p>
          <div className="mt-16 sm:mt-20 flex flex-col sm:flex-row items-center justify-center gap-5 fade-up fade-up-4">
            <Link to="/register" className="ml-btn-primary" data-testid="hero-create-memorial-btn">
              Begin a Memorial
            </Link>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS — three deep-green cards, matched to the
          Begin-Memorial CTA so they read as a single, premium chord. */}
      <section id="how" className="ml-section-sacred">
        <div className="ml-container">
          <div className="text-center max-w-xl mx-auto">
            <h2 className="font-serif font-light text-4xl sm:text-5xl text-[hsl(var(--primary))]">
              Three quiet steps.
            </h2>
            <p
              className="mt-8 text-[16px] leading-relaxed text-[hsl(var(--primary))] font-medium max-w-md mx-auto"
              data-testid="how-tagline"
            >
              Most families finish the first memorial in an afternoon.
              You will keep adding to it for the rest of your life.
            </p>
          </div>
          <div className="mt-24 grid md:grid-cols-3 gap-8 md:gap-6">
            {[
              { step: '01', title: 'Begin with a name.', desc: 'A name, a photograph, the dates that bracket a life. That is all it takes to start.' },
              { step: '02', title: 'Add the living memory.', desc: 'Schools, marriages, children, the places they loved — their Legacy Tree, branch by branch.' },
              { step: '03', title: 'Share a quiet QR.', desc: 'A beautifully printed plaque for a headstone, a bench, a card. One tap and their story opens.' },
            ].map((s) => (
              <div
                key={s.step}
                data-testid={`how-step-${s.step}`}
                className="text-center rounded-sm p-10 transition-shadow duration-500 border border-[#1A3626]/40 hover:shadow-[0_30px_60px_-40px_rgba(26,54,38,0.6)]"
                style={{ backgroundColor: '#1A3626' }}
              >
                <div className="font-serif font-light text-5xl ml-num-display text-[#D9B775]">
                  {s.step}
                </div>
                <h3 className="font-serif text-2xl text-[#F4E5C2] leading-snug mt-6">
                  {s.title}
                </h3>
                <p className="mt-4 text-sm text-[#F4E5C2]/85 leading-relaxed font-light">
                  {s.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STILLNESS — no UI, no buttons, just room to breathe */}
      <section
        aria-label="A moment of stillness"
        className="py-40 sm:py-56"
        data-testid="landing-stillness"
      >
        <div className="ml-container-prose text-center">
          <p className="font-serif font-light italic text-[28px] sm:text-[40px] leading-[1.35] text-[hsl(var(--primary)/0.82)]">
            A place to return to,
            <span className="block mt-3">whenever you need them near.</span>
          </p>
        </div>
      </section>

      {/* WHAT IS MYLEAF — product clarity, no longer abstract.
          One sentence states the product, four numbered steps explain the
          journey from headstone scan to family vault. */}
      <section id="features" className="ml-section-sacred">
        <div className="ml-container">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="font-serif font-light text-4xl sm:text-5xl text-[hsl(var(--primary))] leading-[1.15]">
              Their memory,
              <span className="italic text-[hsl(var(--primary)/0.72)]"> preserved in a QR code.</span>
            </h2>
            <p
              className="mt-8 text-[16px] leading-relaxed text-[hsl(var(--primary))] font-medium max-w-md mx-auto"
              data-testid="features-tagline"
            >
              Placed on a headstone, plaque, or memorial card.
              <span className="block mt-1">Shared with family. Visited forever.</span>
            </p>
          </div>

          {/* 4-step visual journey: scan → story → photos → vault */}
          <div className="mt-24 grid md:grid-cols-4 gap-12 md:gap-8">
            {[
              {
                n: '01',
                title: 'A QR on the headstone.',
                desc: 'Premium QR plaque, sticker, or engraving. Cemetery-friendly. Made to last.',
                icon: <QrPlaqueIcon />,
              },
              {
                n: '02',
                title: 'A single, quiet tap.',
                desc: 'Friends, family, or visitors scan and the memorial opens on any phone.',
                icon: <ScanIcon />,
              },
              {
                n: '03',
                title: 'Their story, beautifully told.',
                desc: 'Photos, life journey, family tree, and a Life Journey video — laid out with care.',
                icon: <StoryIcon />,
              },
              {
                n: '04',
                title: 'A private vault for family.',
                desc: 'Family-only memories behind 24-hour expiring links — no shared passwords.',
                icon: <VaultIcon />,
              },
            ].map((s) => (
              <div key={s.n} className="text-center">
                <div className="flex justify-center text-[hsl(var(--primary)/0.85)] mb-6">{s.icon}</div>
                <div className="ml-num-display text-sm tracking-[0.3em] text-[hsl(var(--accent))]">{s.n}</div>
                <h3 className="font-serif text-xl mt-3 text-[hsl(var(--primary))] leading-snug">{s.title}</h3>
                <p className="mt-3 text-sm text-[hsl(var(--primary)/0.6)] leading-relaxed font-light max-w-[220px] mx-auto">
                  {s.desc}
                </p>
              </div>
            ))}
          </div>

          {/* Single CTA back to a real memorial */}
          <div className="mt-20 text-center">
            <Link
              to="/m/michael-james-anderson"
              className="ml-btn-outline"
              data-testid="features-visit-demo"
            >
              Visit a real memorial →
            </Link>
            <div className="mt-4 text-xs tracking-[0.18em] uppercase text-[hsl(var(--primary)/0.45)]">
              Michael James Anderson · 1950 — 2023
            </div>
          </div>
        </div>
      </section>

      {/* DEMOS — six curated memorials, calm and dignified */}
      <DemoGallery />

      {/* FAMILY VAULT */}
      <section className="ml-section-sacred">
        <div className="ml-container grid md:grid-cols-2 gap-20 items-start">
          <div>
            <h2 className="font-serif font-light text-4xl sm:text-5xl text-[hsl(var(--primary))]">
              A private room,<br />
              <span className="italic text-[hsl(var(--primary)/0.72)]">for family only.</span>
            </h2>
            <p className="mt-8 ml-caption leading-relaxed">
              No shared passwords. No permanent keys. Visitors request access, admins approve,
              and a secure link is sent — quietly expiring in 24 hours.
            </p>
          </div>
          <ul className="divide-y divide-[hsl(var(--primary)/0.08)]">
            {[
              ['Request-based', 'No shared passcodes, ever.'],
              ['24h expiring links', 'Time-bound by default.'],
              ['Audit trail', 'Every access is quietly logged.'],
              ['Revocable', 'Withdrawn in a single click.'],
            ].map(([t, d]) => (
              <li key={t} className="py-6">
                <div className="font-serif text-xl text-[hsl(var(--primary))]">{t}</div>
                <div className="text-sm text-[hsl(var(--primary)/0.6)] mt-2 leading-relaxed font-light">{d}</div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* LEGACY TREE — with actual MyLeaf mark instead of lucide */}
      <section className="ml-section-sacred">
        <div className="ml-container grid md:grid-cols-2 gap-20 items-center">
          <div>
            <h2 className="font-serif font-light text-4xl sm:text-5xl text-[hsl(var(--primary))]">
              Generations,<br />
              <span className="italic text-[hsl(var(--primary)/0.72)]">in a single glance.</span>
            </h2>
            <p className="mt-8 ml-caption leading-relaxed">
              A living olive tree at the heart of every memorial — green leaves for those still
              with us, gold for those who have gone ahead. Each leaf opens a quiet profile card
              of their own.
            </p>
          </div>
          <div className="flex items-center justify-center min-h-[320px]">
            <div className="ml-breathe-heart" style={{ opacity: 0.92 }}>
              <Logo size={220} />
            </div>
          </div>
        </div>
      </section>

      {/* AI MEMORIES — consent-forward, sparse */}
      <section className="ml-section-sacred">
        <div className="ml-container">
          <div className="text-center max-w-xl mx-auto">
            <h2 className="font-serif font-light text-4xl sm:text-5xl text-[hsl(var(--primary))]">
              Gentle AI,
              <span className="italic text-[hsl(var(--primary)/0.72)]"> only if your family agrees.</span>
            </h2>
            <p className="mt-8 ml-caption leading-relaxed">
              Narrated life-story introductions, tender photo animations, read-aloud memories —
              always disabled by default, always with the owner&rsquo;s consent.
            </p>
          </div>
          <div className="mt-24 grid md:grid-cols-3 gap-16">
            {[
              ['Life-story intro', 'A 60-second narrated tribute video, opened on consent.'],
              ['Photo memory', 'A quiet animation of a single photograph.'],
              ['Narrated story', 'Written memories, read aloud in a warm, even voice.'],
            ].map(([t, d]) => (
              <div key={t} className="text-center">
                <h3 className="font-serif text-2xl text-[hsl(var(--primary))]">{t}</h3>
                <p className="text-sm text-[hsl(var(--primary)/0.62)] mt-4 leading-relaxed max-w-xs mx-auto font-light">{d}</p>
                <div className="mt-6 text-[10px] tracking-[0.3em] uppercase text-[hsl(var(--accent))]">
                  Consent required
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING — reframed as 'ways to preserve their memory' */}
      <section id="pricing" className="ml-section-sacred">
        <div className="ml-container">
          <div className="text-center max-w-xl mx-auto">
            <h2 className="font-serif font-light text-4xl sm:text-5xl text-[hsl(var(--primary))]">
              Ways to preserve
              <span className="italic text-[hsl(var(--primary)/0.72)]"> their memory.</span>
            </h2>
            <p className="mt-8 ml-caption leading-relaxed">
              Every memorial is kept with the same care. Choose the room that feels right for your family.
            </p>
          </div>
          <PricingToggle />
        </div>
      </section>

      {/* FAQ */}
      <section className="ml-section-sacred">
        <div className="ml-container grid md:grid-cols-[0.8fr_1fr] gap-20">
          <div>
            <h2 className="font-serif font-light text-4xl sm:text-5xl text-[hsl(var(--primary))]">
              Questions<br />
              <span className="italic text-[hsl(var(--primary)/0.72)]">families ask us.</span>
            </h2>
            <p className="mt-8 ml-caption leading-relaxed">
              If your family is partnered with a cemetery or funeral home, we can quietly arrange white-label setup.
            </p>
          </div>
          <div>
            <Accordion type="single" collapsible className="divide-y divide-[hsl(var(--primary)/0.08)]">
              {[
                ['Who controls the memorial?', 'The Memorial Owner controls billing, admins, AI consent, and can export or delete everything at any time.'],
                ['How is the Family Vault kept private?', 'Visitors request access, admins approve or deny, and approved visitors receive a secure link that expires in 24 hours. Access is revocable and every view is logged.'],
                ['What happens to the QR if we move the plaque?', 'The QR always points to the memorial URL — it never changes. You can download a fresh high-resolution file any time.'],
                ['Is AI required?', 'No. AI Memory Moments are opt-in only. You can leave them disabled forever and the memorial is just as beautiful.'],
                ['Can we export our data?', 'Yes. Only the Memorial Owner can request a full export of photos, videos, and family tree data.'],
              ].map(([q, a], idx) => (
                <AccordionItem key={q} value={`item-${idx}`} className="border-none">
                  <AccordionTrigger
                    className="py-6 text-left font-serif font-light text-xl text-[hsl(var(--primary))] hover:no-underline hover:text-[hsl(var(--primary)/0.75)] transition-colors"
                    data-testid={`faq-q-${idx}`}
                  >
                    {q}
                  </AccordionTrigger>
                  <AccordionContent className="pb-6 text-[15px] text-[hsl(var(--primary)/0.65)] leading-relaxed font-light">
                    {a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* LOCATOR — quiet CTA for families searching for a funeral home or cemetery.
          Sits between the FAQ and the closing banner so it never interrupts the
          emotional arc but is easy to find when a family is planning a service. */}
      <section className="ml-section-sacred">
        <div className="ml-container-prose">
          <div
            className="rounded-sm border border-[hsl(var(--primary)/0.12)] bg-[hsl(var(--surface))] p-10 sm:p-14 text-center"
            data-testid="locator-cta-block"
          >
            <div className="inline-flex w-12 h-12 rounded-full bg-[hsl(var(--accent))]/12 items-center justify-center">
              <MapPin className="w-5 h-5 text-[hsl(var(--accent))]" strokeWidth={1.4} />
            </div>
            <div className="ml-overline mt-6">Locator</div>
            <h2 className="font-serif font-light text-3xl sm:text-4xl mt-4 text-[hsl(var(--primary))] leading-[1.2]">
              Need a funeral home
              <span className="italic text-[hsl(var(--primary)/0.72)]"> or cemetery?</span>
            </h2>
            <p className="mt-6 ml-caption leading-relaxed max-w-lg mx-auto">
              We'll help you find trusted local providers — and quietly pair them with a MyLeaf memorial.
            </p>
            <div className="mt-8">
              <Link
                to="/find-funeral-homes"
                className="ml-btn-outline inline-flex items-center gap-2"
                data-testid="cta-find-funeral-homes"
              >
                <MapPin className="w-4 h-4" /> Find funeral homes &amp; cemeteries
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CLOSING — cream, sacred, no dark banner */}
      <section>
        <div className="ml-container-prose py-32 sm:py-48 text-center">
          <h2 className="font-serif font-light text-4xl sm:text-5xl text-[hsl(var(--primary))] leading-[1.15]">
            Their story is worth keeping.
            <span className="block italic text-[hsl(var(--primary)/0.72)] mt-3">Forever.</span>
          </h2>
          <div className="mt-16 flex flex-col sm:flex-row items-center justify-center gap-5">
            <Link to="/register" className="ml-btn-primary" data-testid="cta-register-btn">
              Begin a Memorial
            </Link>
          </div>
        </div>
      </section>

      {/* COMMUNITY SUPPORT — mission section, calm and responsible */}
      <section className="bg-[hsl(var(--primary))] text-[#F4E5C2]" data-testid="landing-community-support">
        <div className="ml-container py-24 sm:py-32 max-w-4xl">
          <p className="text-xs uppercase tracking-[0.22em] text-[#D9B775]" data-testid="landing-mission-eyebrow">
            Community Support
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl mt-4 leading-[1.2] !text-[#F4E5C2]" data-testid="landing-mission-heading">
            Built with the families we will one day help to carry.
          </h2>
          <p className="mt-6 text-base sm:text-lg leading-relaxed text-[#F4E5C2]/90 max-w-3xl" data-testid="landing-mission-body">
            MyLeaf was started so no family has to grieve and worry at the same time.
            As the MyLeaf community grows, part of future proceeds will support housing
            and assistance initiatives for families in need — with a focus on{' '}
            <span className="text-[#D9B775]">veterans</span>,{' '}
            <span className="text-[#D9B775]">families fighting cancer</span>, and{' '}
            <span className="text-[#D9B775]">parents raising children through hardship</span>.
          </p>
          <p className="mt-5 text-sm text-[#F4E5C2]/75 max-w-3xl" data-testid="landing-mission-disclaimer">
            We won't make promises we can't keep — but every memorial sold brings us closer to the day this work can begin. Special assistance pricing is available today for veterans and hardship cases.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/request-assistance"
              className="ml-btn-gold"
              data-testid="landing-request-assistance-btn"
            >
              Request assistance pricing
            </Link>
          </div>
        </div>
      </section>

      {/* PARTNER WITH MYLEAF — quiet, professional, secondary CTA at the very
          bottom of the landing page. Intentionally subtle so it never
          competes with the family experience. */}
      <section
        className="bg-[#F5EFE2] border-t border-[hsl(var(--primary)/0.08)]"
        data-testid="landing-partner-section"
      >
        <div className="ml-container py-16 sm:py-20 max-w-4xl text-center">
          <div className="ml-overline" data-testid="landing-partner-eyebrow">For providers</div>
          <h2
            className="font-serif font-light text-2xl sm:text-3xl mt-4 text-[hsl(var(--primary))] leading-[1.25]"
            data-testid="landing-partner-heading"
          >
            Partner With MyLeaf
          </h2>
          <p
            className="mt-4 text-[14.5px] sm:text-[15px] leading-relaxed text-[hsl(var(--primary)/0.7)] font-light max-w-2xl mx-auto"
            data-testid="landing-partner-body"
          >
            For funeral homes, cemeteries, and memorial providers interested in offering MyLeaf to families.
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <Link
              to="/become-a-partner"
              className="ml-btn-gold"
              data-testid="landing-become-partner-btn"
            >
              Become a Partner
            </Link>
            <Link
              to="/become-a-partner#partner-model"
              className="ml-btn-ghost"
              data-testid="landing-partner-learn-more-btn"
            >
              Learn More
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

/* Product-clarity icons — gold-stroke geometric, 64×64.
   Used in the "Their memory, preserved in a QR code." section. */
const ICON_STROKE = '#1A3626';
const ICON_ACCENT = '#C5A059';

function QrPlaqueIcon() {
  return (
    <svg viewBox="0 0 64 64" className="w-12 h-12" aria-hidden>
      {/* Plaque outline */}
      <rect x="10" y="6" width="44" height="52" rx="3" fill="none" stroke={ICON_STROKE} strokeWidth="1.4" />
      {/* QR finder */}
      <rect x="18" y="14" width="10" height="10" fill="none" stroke={ICON_STROKE} strokeWidth="1.2" />
      <rect x="36" y="14" width="10" height="10" fill="none" stroke={ICON_STROKE} strokeWidth="1.2" />
      <rect x="18" y="32" width="10" height="10" fill="none" stroke={ICON_STROKE} strokeWidth="1.2" />
      {/* Modules */}
      <rect x="36" y="32" width="2" height="2" fill={ICON_STROKE} />
      <rect x="40" y="34" width="2" height="2" fill={ICON_STROKE} />
      <rect x="44" y="32" width="2" height="2" fill={ICON_STROKE} />
      <rect x="36" y="38" width="2" height="2" fill={ICON_STROKE} />
      <rect x="42" y="40" width="2" height="2" fill={ICON_STROKE} />
      {/* Gold leaf flourish */}
      <line x1="22" y1="50" x2="42" y2="50" stroke={ICON_ACCENT} strokeWidth="1" />
    </svg>
  );
}
function ScanIcon() {
  return (
    <svg viewBox="0 0 64 64" className="w-12 h-12" aria-hidden>
      {/* Phone */}
      <rect x="20" y="6" width="24" height="44" rx="3" fill="none" stroke={ICON_STROKE} strokeWidth="1.4" />
      <line x1="28" y1="46" x2="36" y2="46" stroke={ICON_STROKE} strokeWidth="1.2" />
      {/* Scan beam (gold) */}
      <line x1="24" y1="20" x2="40" y2="20" stroke={ICON_ACCENT} strokeWidth="1.4" />
      <line x1="24" y1="24" x2="40" y2="24" stroke={ICON_ACCENT} strokeWidth="1.4" opacity="0.6" />
      <line x1="24" y1="28" x2="40" y2="28" stroke={ICON_ACCENT} strokeWidth="1.4" opacity="0.35" />
      {/* Tap */}
      <circle cx="32" cy="56" r="3" fill="none" stroke={ICON_STROKE} strokeWidth="1.2" />
    </svg>
  );
}
function StoryIcon() {
  return (
    <svg viewBox="0 0 64 64" className="w-12 h-12" aria-hidden>
      {/* Open page */}
      <path d="M10 12 Q 32 8, 54 12 L 54 52 Q 32 48, 10 52 Z" fill="none" stroke={ICON_STROKE} strokeWidth="1.4" strokeLinejoin="round" />
      <line x1="32" y1="10" x2="32" y2="50" stroke={ICON_STROKE} strokeWidth="1" />
      {/* Photograph block */}
      <rect x="14" y="18" width="14" height="10" fill="none" stroke={ICON_ACCENT} strokeWidth="1" />
      <line x1="14" y1="32" x2="28" y2="32" stroke={ICON_STROKE} strokeWidth="0.8" opacity="0.6" />
      <line x1="14" y1="36" x2="26" y2="36" stroke={ICON_STROKE} strokeWidth="0.8" opacity="0.6" />
      {/* Right page text lines */}
      <line x1="36" y1="20" x2="50" y2="20" stroke={ICON_STROKE} strokeWidth="0.8" opacity="0.6" />
      <line x1="36" y1="24" x2="50" y2="24" stroke={ICON_STROKE} strokeWidth="0.8" opacity="0.6" />
      <line x1="36" y1="28" x2="48" y2="28" stroke={ICON_STROKE} strokeWidth="0.8" opacity="0.6" />
      <line x1="36" y1="36" x2="50" y2="36" stroke={ICON_STROKE} strokeWidth="0.8" opacity="0.45" />
    </svg>
  );
}
function VaultIcon() {
  return (
    <svg viewBox="0 0 64 64" className="w-12 h-12" aria-hidden>
      {/* Lock body */}
      <rect x="14" y="26" width="36" height="28" rx="2" fill="none" stroke={ICON_STROKE} strokeWidth="1.4" />
      {/* Shackle */}
      <path d="M22 26 V 18 a 10 10 0 0 1 20 0 V 26" fill="none" stroke={ICON_STROKE} strokeWidth="1.4" />
      {/* Keyhole */}
      <circle cx="32" cy="38" r="3" fill="none" stroke={ICON_ACCENT} strokeWidth="1.2" />
      <line x1="32" y1="40" x2="32" y2="46" stroke={ICON_ACCENT} strokeWidth="1.2" />
    </svg>
  );
}

/* Ambient leaves — a MIX of vertical fall (most) and horizontal drift
   (some) gives the hero a natural wind feel. Vertical leaves rock as they
   fall; horizontal leaves cross the page slowly, spinning gently. */
function FallingLeaves() {
  const verticals = [
    { left: '16%', d: 28, delay: 0,   sway: 5.8, swayDelay: 0,   r0: -26, r1: 18,  rm: -2,  o: 0.28, scale: 1.0 },
    { left: '48%', d: 34, delay: 7,   sway: 6.8, swayDelay: -2,  r0: 16,  r1: -14, rm: 4,   o: 0.22, scale: 0.78 },
    { left: '78%', d: 30, delay: 14,  sway: 5.2, swayDelay: -3,  r0: -18, r1: 24,  rm: -6,  o: 0.30, scale: 1.15 },
  ];
  const horizontals = [
    { top: '12%', d: 42, delay: 4,  dir: 'lr', o: 0.22, scale: 0.9 },
    { top: '38%', d: 52, delay: 18, dir: 'rl', o: 0.18, scale: 0.75 },
  ];
  return (
    <div aria-hidden className="absolute inset-x-0 top-0 h-[720px] overflow-hidden pointer-events-none">
      {/* Vertical fall + sway */}
      {verticals.map((l, i) => (
        <span
          key={`v-${i}`}
          className="ml-leaf-fall"
          style={{
            left: l.left,
            '--d': `${l.d}s`,
            '--delay': `${l.delay}s`,
            '--o': l.o,
          }}
        >
          <span
            className="ml-leaf-sway"
            style={{
              '--sway': `${l.sway}s`,
              '--sway-delay': `${l.swayDelay}s`,
              '--r0': `${l.r0}deg`,
              '--r1': `${l.r1}deg`,
              '--rm': `${l.rm}deg`,
            }}
          >
            <OliveLeafSvg size={28 * l.scale} />
          </span>
        </span>
      ))}
      {/* Horizontal drift */}
      {horizontals.map((l, i) => (
        <span
          key={`h-${i}`}
          className={l.dir === 'lr' ? 'ml-leaf-drift-lr' : 'ml-leaf-drift-rl'}
          style={{
            top: l.top,
            '--d': `${l.d}s`,
            '--delay': `${l.delay}s`,
            '--o': l.o,
          }}
        >
          <OliveLeafSvg size={24 * l.scale} />
        </span>
      ))}
    </div>
  );
}

/* Realistic olive leaf — asymmetric silhouette with visible veins, a
   slight curl on one edge, and a subtle stem. Reads as a real leaf at
   small sizes and low opacity. */
function OliveLeafSvg({ size = 28 }) {
  return (
    <svg
      width={size}
      height={size * 1.4}
      viewBox="0 0 40 56"
      style={{ display: 'block' }}
    >
      <defs>
        <linearGradient id="olive-leaf-g" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%"  stopColor="#7E9A6E" />
          <stop offset="55%" stopColor="#5E7E4F" />
          <stop offset="100%" stopColor="#3E5A34" />
        </linearGradient>
      </defs>
      {/* Stem */}
      <path
        d="M 20 52 L 20 44"
        stroke="#4D3A20"
        strokeWidth="0.9"
        strokeLinecap="round"
      />
      {/* Leaf body — asymmetric olive silhouette, pointed at both ends */}
      <path
        d="M 20 3
           C 30 10, 35 22, 32 34
           C 29 44, 24 48, 20 50
           C 16 48, 11 44, 8 34
           C 5 22, 10 10, 20 3 Z"
        fill="url(#olive-leaf-g)"
        stroke="#2F4A2A"
        strokeWidth="0.6"
        strokeLinejoin="round"
      />
      {/* Slight highlight — the curl on one edge catching light */}
      <path
        d="M 20 5 C 26 10, 31 20, 29 32"
        fill="none"
        stroke="#A6BE8E"
        strokeWidth="0.7"
        strokeLinecap="round"
        opacity="0.55"
      />
      {/* Central vein */}
      <path
        d="M 20 6 L 20 48"
        stroke="#2F4A2A"
        strokeWidth="0.6"
        strokeLinecap="round"
        opacity="0.75"
      />
      {/* Side veins */}
      <g stroke="#2F4A2A" strokeWidth="0.4" fill="none" strokeLinecap="round" opacity="0.55">
        <path d="M 20 14 Q 14 18, 10 24" />
        <path d="M 20 14 Q 26 18, 30 24" />
        <path d="M 20 24 Q 12 28, 8 34" />
        <path d="M 20 24 Q 28 28, 32 34" />
        <path d="M 20 34 Q 14 40, 12 46" />
        <path d="M 20 34 Q 26 40, 28 46" />
      </g>
    </svg>
  );
}

/* A minimal, decorative QR — kept for any future use; currently unused. */
// eslint-disable-next-line no-unused-vars
function QuietQr() {
  // fixed seed for determinism
  const cells = [
    [1,1,1,1,1,0,0,0,1,1,1,1,1],
    [1,0,0,0,1,0,1,0,1,0,0,0,1],
    [1,0,1,0,1,0,1,0,1,0,1,0,1],
    [1,0,1,0,1,0,0,0,1,0,1,0,1],
    [1,0,1,0,1,1,1,0,1,0,1,0,1],
    [1,0,0,0,1,0,0,0,1,0,0,0,1],
    [1,1,1,1,1,0,1,0,1,1,1,1,1],
    [0,0,0,0,0,0,1,0,0,0,0,0,0],
    [1,0,1,1,0,1,1,1,0,1,1,0,1],
    [0,0,0,1,1,0,0,0,1,0,1,1,0],
    [1,1,0,0,1,1,1,0,1,1,0,0,1],
    [0,1,0,1,0,0,1,0,0,1,0,1,0],
    [1,0,1,0,1,1,1,1,1,0,1,0,1],
  ];
  return (
    <svg viewBox="0 0 13 13" className="w-28 h-28" aria-hidden>
      {cells.map((row, r) =>
        row.map((c, x) => (c ? (
          <rect key={`${r}-${x}`} x={x} y={r} width="1" height="1" fill="#1A3626" />
        ) : null))
      )}
    </svg>
  );
}

function PricingToggle() {
  const [plans, setPlans] = useState([]);
  useEffect(() => {
    // iter68 — defensive: always pass an array to setPlans so the
    // landing page never crashes with "undefined.map" if the API
    // returns an unexpected shape (e.g. proxy intercepts the call
    // and replies with HTML).
    api.get('/packages')
      .then(({ data }) => setPlans(Array.isArray(data?.packages) ? data.packages : []))
      .catch(() => setPlans([]));
  }, []);

  return (
    <div className="mt-16 grid md:grid-cols-2 gap-6 md:gap-8 max-w-4xl mx-auto">
      {plans.map((p) => {
        const isMemorial = p.id === 'memorial';
        return (
          <div
            key={p.id}
            data-testid={`pricing-card-${p.id}`}
            className={
              'relative flex flex-col p-10 border transition-all duration-500 ' +
              (isMemorial
                ? 'border-[#1A3626] shadow-[0_30px_60px_-40px_rgba(26,54,38,0.7)]'
                : p.highlighted
                ? 'border-[hsl(var(--accent)/0.55)] bg-[hsl(var(--surface))] shadow-[0_30px_60px_-50px_rgba(26,54,38,0.55)]'
                : 'border-[hsl(var(--primary)/0.1)] bg-transparent hover:border-[hsl(var(--primary)/0.2)]')
            }
            style={isMemorial ? { backgroundColor: '#1A3626' } : undefined}
          >
            {p.highlighted && !isMemorial && (
              <>
                <div className="absolute -top-px left-0 right-0 h-px bg-[hsl(var(--accent))]" aria-hidden />
                <div
                  className="self-start mb-5 inline-flex items-center gap-2 rounded-full border border-[hsl(var(--accent))]/55 bg-[hsl(var(--accent))]/10 px-3 py-1 text-[11px] tracking-[0.22em] uppercase font-semibold text-[hsl(var(--accent))]"
                  data-testid="includes-forever-here-collection"
                >
                  <span aria-hidden className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--accent))]" />
                  Includes Forever Here Collection
                </div>
              </>
            )}

            <h3
              className={
                'font-serif font-light text-[30px] sm:text-[34px] leading-tight ' +
                (isMemorial ? 'text-[#F4E5C2]' : 'text-[hsl(var(--primary))]')
              }
            >
              {p.name}
            </h3>
            <p
              className={
                'text-sm font-light mt-3 min-h-[44px] leading-relaxed ' +
                (isMemorial ? 'text-[#F4E5C2]/85' : 'text-[hsl(var(--primary)/0.58)]')
              }
            >
              {p.tagline}
            </p>

            <div className="mt-10 flex items-baseline gap-2">
              <span
                className={
                  'ml-num-display text-[56px] leading-none ' +
                  (isMemorial ? 'text-[#F4E5C2]' : 'text-[hsl(var(--primary))]')
                }
              >
                ${Number(p.price_setup ?? p.price).toFixed(2)}
              </span>
              <span
                className={
                  'text-xs tracking-[0.22em] uppercase ' +
                  (isMemorial ? 'text-[#D9B775]' : 'text-[hsl(var(--primary)/0.5)]')
                }
              >
                one-time
              </span>
            </div>
            <div
              className={
                'mt-2 text-[11px] tracking-[0.18em] uppercase ' +
                (isMemorial ? 'text-[#D9B775]' : 'text-[hsl(var(--primary)/0.55)]')
              }
            >
              + ${p.keeping_fee ?? p.price_annual} / year keeping fee
            </div>

            <div
              className={'w-full my-8 h-px ' + (isMemorial ? 'bg-[#D9B775]/35' : 'ml-divider-leaf')}
              aria-hidden
            />

            <ul
              className={
                'space-y-4 text-sm flex-1 font-light ' +
                (isMemorial ? 'text-[#F4E5C2]/95' : 'text-[hsl(var(--primary)/0.78)]')
              }
            >
              {p.features.map((f) => (
                <li key={f} className="flex items-start gap-3">
                  <Check
                    className={
                      'w-4 h-4 mt-0.5 ' + (isMemorial ? 'text-[#D9B775]' : 'text-[hsl(var(--accent))]')
                    }
                    strokeWidth={1.5}
                  />{' '}
                  {f}
                </li>
              ))}
            </ul>

            <Link
              to={`/register?plan=${p.id}`}
              className={
                isMemorial
                  ? 'mt-10 inline-flex items-center justify-center gap-2 rounded-sm bg-[#D9B775] text-[#1A3626] px-8 py-3 text-[11px] font-semibold tracking-[0.22em] uppercase transition-all duration-300 hover:bg-[#F4E5C2]'
                  : `${p.highlighted ? 'ml-btn-gold' : 'ml-btn-outline'} mt-10`
              }
              data-testid={`select-plan-${p.id}`}
            >
              Begin with {p.name}
            </Link>
          </div>
        );
      })}
    </div>
  );
}


/* DemoGallery — a calm, ever-evolving window into the lives families
   are choosing to keep here. Four portraits at a time, each gently fading
   in and out as new memorials are created. Powered live by
   /api/public/demos so the gallery always reflects the current roster. */
function DemoGallery() {
  const [demos, setDemos] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [offset, setOffset] = useState(0);
  // When the user clicks "Visit a memorial", we drop the 6-row rotating
  // window and show every profile in the roster at once.
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    api.get('/public/demos')
      .then(({ data }) => setDemos(data.demos || []))
      .catch(() => setDemos([]))
      .finally(() => setLoaded(true));
  }, []);

  // Rotate the visible window every 7s so new memorials cycle through
  // without flashy carousel motion — simple fade, no slide. Pauses entirely
  // once the user has expanded the gallery.
  useEffect(() => {
    if (showAll) return;
    if (demos.length <= 6) return;
    const id = setInterval(() => {
      setOffset((o) => (o + 3) % demos.length);
    }, 7000);
    return () => clearInterval(id);
  }, [demos.length, showAll]);

  if (loaded && demos.length === 0) return null;

  // Show up to 6 at a time, cycling through the full roster. We build the
  // window modulo-style so rotation never shows an empty slot. Once
  // `showAll` flips, we render the entire roster in one calm grid.
  const WINDOW_SIZE = 6;
  const visible = showAll
    ? demos
    : demos.length <= WINDOW_SIZE
      ? demos
      : Array.from({ length: WINDOW_SIZE }, (_, i) => demos[(offset + i) % demos.length]);

  return (
    <section
      id="demos"
      className="ml-section-sacred"
      data-testid="landing-demos"
    >
      <div className="ml-container">
        {/* Calm CTA introducing the section — replaces the dual hero CTA. */}
        {!showAll && demos.length > 0 && (
          <div className="text-center mb-16">
            <button
              type="button"
              onClick={() => setShowAll(true)}
              className="ml-btn-gold"
              data-testid="visit-memorial-btn"
            >
              Visit a memorial →
            </button>
          </div>
        )}

        <div className="text-center max-w-2xl mx-auto">
          <div className="ml-overline">Lives we honour</div>
          <h2 className="font-serif font-light text-4xl sm:text-5xl text-[hsl(var(--primary))] mt-5 tracking-tight">
            A quiet roster of <br className="hidden sm:block" />
            <span className="italic text-[hsl(var(--primary)/0.72)]">
              the families we keep.
            </span>
          </h2>
          <p className="mt-6 ml-caption leading-relaxed">
            Every name here is a memorial someone chose to build on MyLeaf —
            a parent, a grandparent, a dear friend. Take a moment with any of them.
          </p>
        </div>

        <div
          className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14 max-w-5xl mx-auto"
          data-testid="landing-demos-grid"
        >
          {visible.map((d) => {
            const dob = (d.date_of_birth || '').slice(0, 4);
            const dod = (d.date_of_passing || '').slice(0, 4);
            const years = dob && dod ? `${dob} — ${dod}` : '';
            return (
              <Link
                key={`${d.slug}-${showAll ? 'all' : offset}`}
                to={`/m/${d.slug}`}
                data-testid={`demo-card-${d.slug}`}
                className="group flex flex-col items-center text-center ml-demo-fade-in"
              >
                <div className="relative">
                  <div className="absolute -inset-2 rounded-full bg-[hsl(var(--accent))]/10 blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                  <div className="relative w-32 h-32 sm:w-36 sm:h-36 rounded-full overflow-hidden border border-[hsl(var(--primary)/0.14)] bg-[hsl(var(--muted))]">
                    {d.profile_photo_url ? (
                      <img
                        src={d.profile_photo_url}
                        alt={d.full_name}
                        className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-[1.04]"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center font-serif font-light text-3xl text-[hsl(var(--primary))]">
                        {(d.full_name || '?')[0]}
                      </div>
                    )}
                  </div>
                </div>
                <h3 className="font-serif text-2xl mt-7 text-[hsl(var(--primary))] tracking-tight">
                  {d.full_name}
                </h3>
                {years && (
                  <div className="mt-2 text-[10px] tracking-[0.28em] uppercase font-medium text-[hsl(var(--primary)/0.5)]">
                    {years}
                  </div>
                )}
                {d.birthplace && (
                  <div className="mt-1 text-[11px] text-[hsl(var(--primary)/0.55)] font-light">
                    {d.birthplace}
                  </div>
                )}
                <span className="mt-5 text-[11px] tracking-[0.22em] uppercase font-medium text-[hsl(var(--accent))] group-hover:text-[hsl(var(--primary))] transition-colors">
                  View memorial →
                </span>
              </Link>
            );
          })}
        </div>

        {showAll && demos.length > WINDOW_SIZE && (
          <div className="mt-12 text-center">
            <button
              type="button"
              onClick={() => setShowAll(false)}
              className="ml-btn-ghost"
              data-testid="visit-memorial-collapse"
            >
              Show fewer ↑
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
